// NFL Scouting Combine measurement notation utilities.
// Storage is always decimal inches / seconds. Display is NFL-style codes.

// Eighths-notation for hand, arm, wingspan (IIE8 format).
// 9 4/8" → "0948"   |   72 1/8" → "7218"   |   32 5/8" → "3258"
export function toEighths(inches) {
  if (inches == null || inches === "" || isNaN(Number(inches))) return "";
  const totalEighths = Math.round(Number(inches) * 8);
  const wholeIn = Math.floor(totalEighths / 8);
  const eighths = totalEighths - wholeIn * 8;
  return `${String(wholeIn).padStart(2, "0")}${eighths}8`;
}

// Parse NFL eighths notation OR plain decimal into inches.
// "0948" → 9.5    "9.5" → 9.5    "9 4/8" → 9.5
export function parseEighths(str) {
  if (str == null || str === "") return null;
  if (typeof str === "number") return str;
  const s = String(str).trim().replace(/\s+/g, " ");
  // 4-digit NFL code: IIE8
  if (/^\d{4}$/.test(s)) {
    const whole = parseInt(s.slice(0, 2), 10);
    const eighths = parseInt(s.slice(2, 3), 10);
    if (eighths > 7) return null;
    return whole + eighths / 8;
  }
  // "9 4/8"
  const frac = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (frac) return parseInt(frac[1], 10) + parseInt(frac[2], 10) / parseInt(frac[3], 10);
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// Height notation FIIE — feet, inches (2 digits), eighths.
// 6'2 4/8" (= 74.5 in) → "6024"   |   5'11" (= 71 in) → "5110"
export function toHeightCode(inches) {
  if (inches == null || inches === "" || isNaN(Number(inches))) return "";
  const totalEighths = Math.round(Number(inches) * 8);
  const totalInches = Math.floor(totalEighths / 8);
  const eighths = totalEighths - totalInches * 8;
  const feet = Math.floor(totalInches / 12);
  const inch = totalInches - feet * 12;
  return `${feet}${String(inch).padStart(2, "0")}${eighths}`;
}

// Parse NFL height code OR decimal inches.
// "6024" → 74.5   |   "5110" → 71   |   "74.5" → 74.5
export function parseHeight(str) {
  if (str == null || str === "") return null;
  if (typeof str === "number") return str;
  const s = String(str).trim();
  if (/^\d{4}$/.test(s)) {
    const feet = parseInt(s.slice(0, 1), 10);
    const inch = parseInt(s.slice(1, 3), 10);
    const eighths = parseInt(s.slice(3, 4), 10);
    if (inch > 11 || eighths > 7) return null;
    return feet * 12 + inch + eighths / 8;
  }
  // Allow "6'2" or "6'2 4/8"
  const m = s.match(/^(\d+)'(\d+)(?:\s+(\d+)\/(\d+))?"?$/);
  if (m) {
    const feet = parseInt(m[1], 10);
    const inch = parseInt(m[2], 10);
    const e = m[3] ? parseInt(m[3], 10) / parseInt(m[4], 10) : 0;
    return feet * 12 + inch + e;
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// Pretty: "6'2 4/8\""  or  "6'2\""
export function formatHeightPretty(inches) {
  if (inches == null || inches === "" || isNaN(Number(inches))) return "—";
  const totalEighths = Math.round(Number(inches) * 8);
  const totalInches = Math.floor(totalEighths / 8);
  const eighths = totalEighths - totalInches * 8;
  const feet = Math.floor(totalInches / 12);
  const inch = totalInches - feet * 12;
  const frac = eighths === 0 ? "" : ` ${eighths}/8`;
  return `${feet}'${inch}${frac}"`;
}

// Pretty: "9 4/8\""
export function formatEighthsPretty(inches) {
  if (inches == null || inches === "" || isNaN(Number(inches))) return "—";
  const totalEighths = Math.round(Number(inches) * 8);
  const wholeIn = Math.floor(totalEighths / 8);
  const eighths = totalEighths - wholeIn * 8;
  return eighths === 0 ? `${wholeIn}"` : `${wholeIn} ${eighths}/8"`;
}

// Broad jump: stored as decimal inches, displayed as 9'8"
export function formatBroad(inches) {
  if (inches == null || inches === "" || isNaN(Number(inches))) return "—";
  const v = Number(inches);
  const f = Math.floor(v / 12);
  const i = Math.round(v - f * 12);
  return `${f}'${i}"`;
}
export function broadToInches(feet, inches) {
  return Number(feet) * 12 + Number(inches);
}

// 40-yard / shuttle / 3-cone (seconds) → "4.58"
export function formatSeconds(s) {
  if (s == null || s === "" || isNaN(Number(s))) return "—";
  return Number(s).toFixed(2);
}

// Vertical: "34.5\""
export function formatVertical(inches) {
  if (inches == null || inches === "" || isNaN(Number(inches))) return "—";
  const v = Number(inches);
  return Number.isInteger(v) ? `${v}"` : `${v}"`;
}
